import { useNavigate } from 'react-router-dom'

interface UpgradePromptProps {
  message: string
  code?: string // limit_exceeded or upgrade_required
  limitType?: string // agents, projects, code_review, etc.
  onDismiss?: () => void
}

interface ContextualContent {
  title: string
  description: string
  cta: string
}

function getContextualContent(limitType?: string, code?: string): ContextualContent {
  switch (limitType) {
    case 'agents':
      return {
        title: 'Your AI team is at capacity',
        description: 'Free plan includes 1 concurrent agent. Pro unlocks 5 parallel agents — ship features 5x faster.',
        cta: 'Unlock Parallel Agents',
      }
    case 'projects':
      return {
        title: 'Project limit reached',
        description: "You've used all 5 free projects. Upgrade to Pro for unlimited projects and keep building.",
        cta: 'Get Unlimited Projects',
      }
    case 'code_review':
      return {
        title: 'AI Code Review',
        description: 'Automated code review checks architecture, security, and style on every PR. Available on Pro.',
        cta: 'Enable Code Review',
      }
    case 'agent_minutes':
      return {
        title: 'Agent time exhausted',
        description: "Your agents have used all allocated minutes this month. Pro includes more agent time for bigger features.",
        cta: 'Get More Agent Time',
      }
    case 'api_calls':
      return {
        title: 'API limit reached',
        description: 'Daily API call limit reached. Pro includes significantly higher API limits.',
        cta: 'Increase API Limits',
      }
    case 'team_members':
      return {
        title: 'Team is full',
        description: 'Invite more team members by upgrading. Team plan supports up to 20 seats.',
        cta: 'Expand Your Team',
      }
    case 'workflows':
    case 'workflow_executions':
      return {
        title: 'Workflow limit reached',
        description: 'Create more workflow pipelines and run more executions with Pro.',
        cta: 'Unlock More Workflows',
      }
    case 'test_runs':
      return {
        title: 'Test run limit reached',
        description: 'Run more Docker-based tests with a Pro plan.',
        cta: 'Get More Test Runs',
      }
    case 'docker_infra':
      return {
        title: 'Docker infrastructure',
        description: 'Containerized builds, tests, and agent isolation require Pro.',
        cta: 'Enable Docker',
      }
    default:
      return {
        title: code === 'upgrade_required' ? 'Upgrade Required' : 'Limit Reached',
        description: 'Upgrade your plan to unlock this feature and keep building.',
        cta: 'View Plans',
      }
  }
}

export function UpgradePrompt({ message, code, limitType, onDismiss }: UpgradePromptProps) {
  const navigate = useNavigate()
  const ctx = getContextualContent(limitType, code)

  return (
    <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-gradient-to-br from-primary-50 to-orange-50 dark:from-primary-900/20 dark:to-orange-900/20 p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-orange-500 flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {ctx.title}
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{ctx.description}</p>
          {message !== ctx.description && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{message}</p>
          )}
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={() => navigate('/billing')}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-orange-500 rounded-lg hover:from-primary-500 hover:to-orange-400 transition-all shadow-sm"
            >
              {ctx.cta}
            </button>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Maybe later
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
